/**
 * Collections workspace — follow-up queue, case drawer (discussions +
 * promises), and manager oversight (who handles which clients).
 */
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, CalendarClock, HandCoins, Loader2, Phone, RefreshCw, UserCheck,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ExportButton } from "@/components/staff/ExportButton";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";
import { useStaff } from "./StaffShell";

type Row = Record<string, any>;

const BUCKET_TONE: Record<string, string> = {
  "1-7": "bg-primary/10 text-primary",
  "8-30": "bg-warning/10 text-warning",
  "31-60": "bg-warning/20 text-warning",
  "61-90": "bg-destructive/10 text-destructive",
  "90+": "bg-destructive/20 text-destructive",
};

const CHANNELS = [
  { value: "call_out", label: "Call (outbound)" },
  { value: "call_in", label: "Call (inbound)" },
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "field_visit", label: "Field visit" },
  { value: "letter", label: "Letter / demand notice" },
];

const OUTCOMES = [
  { value: "reached_promised", label: "Reached — promised to pay" },
  { value: "partial_agreed", label: "Reached — partial payment agreed" },
  { value: "reached_refused", label: "Reached — refused" },
  { value: "reached_disputed", label: "Reached — disputes the debt" },
  { value: "no_answer", label: "No answer" },
  { value: "unreachable", label: "Phone off / unreachable" },
  { value: "wrong_number", label: "Wrong number" },
  { value: "third_party_message", label: "Left message with third party" },
  { value: "visit_met", label: "Visit — met the client" },
  { value: "visit_not_found", label: "Visit — not found" },
];
const TERMINAL = ["wrong_number", "reached_disputed"];

const todayISO = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export function CollectionsPage() {
  const staff = useStaff();
  const { toast } = useToast();
  const isManager = ["regional_manager", "national_manager", "admin"].includes(staff?.role || "");

  const [buckets, setBuckets] = useState<Row | null>(null);
  const [cases, setCases] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState<string | null>(null);
  const [dueToday, setDueToday] = useState(false);
  const [mine, setMine] = useState(false);
  const [openCase, setOpenCase] = useState<Row | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (bucket) params.set("bucket", bucket);
    if (dueToday) params.set("due_today", "true");
    if (mine) params.set("mine", "true");
    try {
      const [b, q] = await Promise.all([
        staffApi.collectionBuckets(),
        staffApi.collectionQueue(params.toString() ? `?${params}` : ""),
      ]);
      setBuckets(b);
      setCases(q.cases);
    } catch (error) {
      toast({ title: "Could not load the queue", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [bucket, dueToday, mine, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Collections</h1>
          <p className="text-sm text-muted-foreground">
            Clients due and overdue — how long, who's handling them, what was discussed.
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton dataset="collections"
            params={bucket ? `?bucket=${encodeURIComponent(bucket)}` : ""} />
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="mr-1 h-4 w-4" />Refresh
          </Button>
        </div>
      </header>

      <Tabs defaultValue="queue">
        <TabsList>
          <TabsTrigger value="queue">Follow-up queue</TabsTrigger>
          {isManager && <TabsTrigger value="oversight">Team oversight</TabsTrigger>}
        </TabsList>

        <TabsContent value="queue" className="space-y-4 pt-4">
          {/* Aging chips */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={!bucket && !dueToday ? "default" : "outline"}
              onClick={() => { setBucket(null); setDueToday(false); }}>
              All overdue
              {buckets && <span className="ml-2 tabular-nums opacity-80">{formatCurrency(buckets.total_at_risk)}</span>}
            </Button>
            <Button size="sm" variant={dueToday ? "default" : "outline"}
              onClick={() => { setDueToday(!dueToday); setBucket(null); }}>
              <CalendarClock className="mr-1 h-4 w-4" />
              My day {buckets ? `(${buckets.due_today})` : ""}
            </Button>
            {buckets?.buckets.map((b: Row) => (
              <Button key={b.label} size="sm" variant={bucket === b.label ? "default" : "outline"}
                onClick={() => { setBucket(bucket === b.label ? null : b.label); setDueToday(false); }}>
                {b.label} days
                <span className="ml-2 tabular-nums opacity-80">
                  {b.count} · {formatCurrency(b.amount)}
                </span>
              </Button>
            ))}
            {isManager && (
              <Button size="sm" variant={mine ? "secondary" : "ghost"} onClick={() => setMine(!mine)}>
                Assigned to me
              </Button>
            )}
          </div>

          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin" />
              ) : cases.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  Nothing overdue in this view — the book is current.
                </p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Overdue</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Last contact</TableHead>
                    <TableHead>Promise</TableHead>
                    <TableHead>Next action</TableHead>
                    <TableHead className="text-right">Case</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {cases.map((c) => (
                      <TableRow key={c.id} className="cursor-pointer" onClick={() => setOpenCase(c)}>
                        <TableCell>
                          <p className="font-medium">{c.borrower_name || "—"}</p>
                          <p className="text-xs text-muted-foreground">{c.borrower_phone}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={BUCKET_TONE[c.bucket] || "bg-muted"}>
                            {c.days_past_due} days
                          </Badge>
                          {c.broken_promise_count > 0 && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                              <AlertTriangle className="h-3 w-3" />
                              {c.broken_promise_count} broken promise{c.broken_promise_count > 1 ? "s" : ""}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          <p className="font-medium">{formatCurrency(c.amount_overdue)}</p>
                          <p className="text-xs text-muted-foreground">
                            of {c.outstanding != null ? formatCurrency(c.outstanding) : "—"}
                          </p>
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.last_contact ? (
                            <>
                              <p>{new Date(c.last_contact.at).toLocaleDateString()}</p>
                              <p className="text-muted-foreground">
                                {String(c.last_contact.outcome).split("_").join(" ")}
                              </p>
                            </>
                          ) : <span className="text-warning">never contacted</span>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.active_promise ? (
                            <span className="text-success">
                              {formatCurrency(c.active_promise.amount)} by{" "}
                              {new Date(c.active_promise.date).toLocaleDateString()}
                            </span>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.next_action_date
                            ? new Date(c.next_action_date).toLocaleDateString()
                            : "—"}
                          <p className="text-muted-foreground">{c.stage_label}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost">Open</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isManager && (
          <TabsContent value="oversight" className="pt-4">
            <OversightTab />
          </TabsContent>
        )}
      </Tabs>

      <CaseDrawer caseRow={openCase} onClose={() => setOpenCase(null)} onChanged={load} />
    </div>
  );
}

// ============ Case drawer ============

function CaseDrawer({ caseRow, onClose, onChanged }: {
  caseRow: Row | null; onClose: () => void; onChanged: () => void;
}) {
  const { toast } = useToast();
  const [detail, setDetail] = useState<Row | null>(null);
  const [loading, setLoading] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [promiseOpen, setPromiseOpen] = useState(false);

  const [channel, setChannel] = useState("call_out");
  const [outcome, setOutcome] = useState("no_answer");
  const [notes, setNotes] = useState("");
  const [nextDate, setNextDate] = useState(inDays(2));
  const [nextAction, setNextAction] = useState("");
  const [promiseAmount, setPromiseAmount] = useState("");
  const [promiseDate, setPromiseDate] = useState(inDays(3));
  const [busy, setBusy] = useState(false);

  const loadDetail = useCallback(async (id: string) => {
    setLoading(true);
    try {
      setDetail(await staffApi.collectionCase(id));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (caseRow?.id) loadDetail(caseRow.id);
    else setDetail(null);
  }, [caseRow?.id, loadDetail]);

  const submitLog = async () => {
    if (!caseRow) return;
    setBusy(true);
    try {
      await staffApi.logInteraction(caseRow.id, {
        channel, outcome, notes: notes || undefined,
        next_action: nextAction || undefined,
        next_action_date: TERMINAL.includes(outcome) ? undefined : nextDate,
      });
      toast({ title: "Discussion logged" });
      setLogOpen(false); setNotes(""); setNextAction("");
      await loadDetail(caseRow.id);
      onChanged();
    } catch (error) {
      toast({ title: "Could not log", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const submitPromise = async () => {
    if (!caseRow) return;
    setBusy(true);
    try {
      await staffApi.takePromise(caseRow.id, {
        promised_amount: Number(promiseAmount), promised_date: promiseDate,
      });
      toast({ title: "Promise recorded", description: "It closes itself when the payment lands." });
      setPromiseOpen(false); setPromiseAmount("");
      await loadDetail(caseRow.id);
      onChanged();
    } catch (error) {
      toast({ title: "Could not record promise", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Sheet open={Boolean(caseRow)} onOpenChange={(open) => !open && onClose()}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{caseRow?.borrower_name || "Case"}</SheetTitle>
          </SheetHeader>

          {loading || !detail ? (
            <Loader2 className="mx-auto my-12 h-6 w-6 animate-spin" />
          ) : (
            <div className="space-y-5 py-4">
              {/* Header facts */}
              <div className="rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <a href={`tel:${detail.borrower?.phone}`} className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Phone className="h-4 w-4" />{detail.borrower?.phone}
                  </a>
                  <Badge className={BUCKET_TONE[detail.bucket] || "bg-muted"}>
                    {detail.days_past_due} days overdue
                  </Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Amount overdue</p>
                    <p className="font-semibold tabular-nums">{formatCurrency(detail.amount_overdue)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total outstanding</p>
                    <p className="font-semibold tabular-nums">
                      {detail.loan ? formatCurrency(detail.loan.outstanding) : "—"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Stage</p>
                    <p>{detail.stage_label}</p>
                  </div>
                </div>
                {detail.broken_promise_count > 0 && (
                  <p className="mt-3 flex items-center gap-1 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    {detail.broken_promise_count} broken promise(s) on this case
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => setLogOpen(true)}>Log discussion</Button>
                <Button className="flex-1" variant="outline" onClick={() => setPromiseOpen(true)}>
                  <HandCoins className="mr-1 h-4 w-4" />Take promise
                </Button>
              </div>

              {/* Timeline */}
              <div>
                <p className="mb-2 text-sm font-medium">History</p>
                {detail.timeline.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nothing logged yet — this client has not been contacted.
                  </p>
                ) : (
                  <ol className="space-y-3 border-l pl-4">
                    {detail.timeline.map((e: Row, i: number) => (
                      <li key={i} className="relative text-sm">
                        <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                        <p className="text-xs text-muted-foreground">
                          {new Date(e.at).toLocaleString()}
                        </p>
                        {e.type === "interaction" && (
                          <>
                            <p className="font-medium">
                              {String(e.channel).split("_").join(" ")} — {String(e.outcome).split("_").join(" ")}
                            </p>
                            {e.notes && <p className="text-muted-foreground">{e.notes}</p>}
                            {e.next_action && <p className="text-xs">Next: {e.next_action}</p>}
                          </>
                        )}
                        {e.type === "promise" && (
                          <p className="font-medium">
                            Promised {formatCurrency(e.amount)} by {new Date(e.date).toLocaleDateString()}{" "}
                            <Badge className={
                              e.status === "kept" ? "bg-success/10 text-success"
                                : e.status === "broken" ? "bg-destructive/10 text-destructive"
                                : "bg-muted"}>{e.status}</Badge>
                          </p>
                        )}
                        {e.type === "payment" && (
                          <p className="font-medium text-success">
                            Payment received {formatCurrency(e.amount)}
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Schedule */}
              <div>
                <p className="mb-2 text-sm font-medium">Repayment schedule</p>
                <div className="space-y-1 text-sm">
                  {detail.installments.map((i: Row) => (
                    <div key={i.sequence} className="flex items-center justify-between rounded border px-3 py-1.5">
                      <span>{i.due_date ? new Date(i.due_date).toLocaleDateString() : "—"}</span>
                      <span className="tabular-nums">{formatCurrency(i.amount_due)}</span>
                      <Badge className={
                        i.status === "paid" ? "bg-success/10 text-success"
                          : i.status === "partial" ? "bg-warning/10 text-warning"
                          : "bg-muted text-muted-foreground"}>{i.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Log discussion */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log a discussion</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>What happened</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="What the client said, commitments, context" />
            </div>
            {!TERMINAL.includes(outcome) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Next follow-up</Label>
                  <Input type="date" min={todayISO()} value={nextDate}
                    onChange={(e) => setNextDate(e.target.value)} />
                </div>
                <div>
                  <Label>Next action</Label>
                  <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)}
                    placeholder="Call again / visit" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpen(false)}>Cancel</Button>
            <Button onClick={submitLog} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Take promise */}
      <Dialog open={promiseOpen} onOpenChange={setPromiseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record a promise to pay</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            It closes automatically when a matching payment arrives, and reopens the case if the date passes unpaid.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (K)</Label>
              <Input inputMode="decimal" value={promiseAmount}
                onChange={(e) => setPromiseAmount(e.target.value.replace(/[^\d.]/g, ""))} />
            </div>
            <div>
              <Label>By date</Label>
              <Input type="date" min={todayISO()} value={promiseDate}
                onChange={(e) => setPromiseDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromiseOpen(false)}>Cancel</Button>
            <Button onClick={submitPromise} disabled={busy || !Number(promiseAmount)}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record promise"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============ Oversight ============

function OversightTab() {
  const { toast } = useToast();
  const [data, setData] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await staffApi.collectionsOversight());
    } catch (error) {
      toast({ title: "Could not load oversight", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin" />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {data.unassigned.count > 0 && (
        <Card className="border-warning/40">
          <CardContent className="flex items-center gap-3 py-4">
            <UserCheck className="h-5 w-5 text-warning" />
            <p className="text-sm">
              <span className="font-medium">{data.unassigned.count} unassigned case(s)</span>{" "}
              worth {formatCurrency(data.unassigned.value_at_risk)} — nobody is following these clients up.
            </p>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Who is handling which clients</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Collector</TableHead>
              <TableHead>Cases</TableHead>
              <TableHead>Value at risk</TableHead>
              <TableHead>Contacts logged</TableHead>
              <TableHead>Promises</TableHead>
              <TableHead>Kept-promise rate</TableHead>
              <TableHead>Stale (7d+)</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.collectors.map((c: Row) => (
                <TableRow key={c.staff_id}>
                  <TableCell>
                    <p className="font-medium">{c.name || "—"}</p>
                    <p className="text-xs capitalize text-muted-foreground">
                      {String(c.role || "").split("_").join(" ")}
                    </p>
                  </TableCell>
                  <TableCell className="tabular-nums">{c.cases}</TableCell>
                  <TableCell className="tabular-nums">{formatCurrency(c.value_at_risk)}</TableCell>
                  <TableCell className="tabular-nums">{c.contacts_logged}</TableCell>
                  <TableCell className="tabular-nums">{c.promises_taken}</TableCell>
                  <TableCell className="tabular-nums">
                    {c.kept_promise_rate == null ? "—" : (
                      <span className={c.kept_promise_rate >= 70 ? "text-success" : "text-warning"}>
                        {c.kept_promise_rate}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={c.stale_cases > 0 ? "font-medium text-warning tabular-nums" : "tabular-nums"}>
                    {c.stale_cases}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
